import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Alert,
  Checkbox,
  Snackbar,
  ListSubheader,
  Menu,
  Popper,
  Collapse
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TuneIcon from '@mui/icons-material/Tune';
import directIcon from '../assets/direct.png';
import ollamaIcon from '../assets/ollama.png';
import chatGptIcon from '../assets/chatgpt.jpg';
import googleAiIcon from '../assets/googleai.svg';
import openRouterIcon from '../assets/openrouter.jpeg';
import groqIcon from '../assets/groq.svg';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { firewallService, ollamaService, openaiService, googleAiStudioService, openRouterService, groqService, claudeService, syslogService } from '../services/api';
import authService from '../services/auth';
import { formatLocalDateTime } from '../utils/date';

// Helper to derive alert level from audit score (matches SIEM Tab logic)
const getAlertLevelFromScore = (score) => {
  if (score === null || score === undefined || isNaN(Number(score))) return null;
  const value = Number(score);
  if (value < 30) return 'Critical Alert';
  if (value < 50) return 'High Alert';
  if (value < 80) return 'Medium Alert';
  if (value < 96) return 'Low Alert';
  return 'Compliant';
};

const getAlertChipStyle = (alertLevel) => {
  switch (alertLevel) {
    case 'Critical Alert':
      return { backgroundColor: '#d32f2f', color: '#fff' };
    case 'High Alert':
      return { backgroundColor: '#f57c00', color: '#fff' };
    case 'Medium Alert':
      return { backgroundColor: '#fbc02d', color: 'rgba(0,0,0,0.87)' };
    case 'Low Alert':
      return { backgroundColor: '#388e3c', color: '#fff' };
    case 'Compliant':
      return { backgroundColor: '#0d47a1', color: '#fff' };
    default:
      return { backgroundColor: '#616161', color: '#fff' };
  }
};

function History() {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isAdmin = authService.isAdmin();
  const storedUser = authService.getStoredUser();
  const userFilterKey = `nscat_filters_${storedUser?.id || storedUser?.username || 'default'}`;
  const userSortKey = `nscat_sort_${storedUser?.id || storedUser?.username || 'default'}`;

  const defaultFilters = {
    startDate: '',
    endDate: '',
    benchmarkType: '',
    aiModel: '',
    deviceId: '',
    socAlert: '',
    reportStatus: '',
    minScore: '',
    maxScore: '',
    minDuration: '',
    maxDuration: '',
    minObs: '',
    maxObs: '',
    obsCategory: ''
  };

  const defaultSort = { field: 'audit_date', direction: 'desc' };

  const [alertCounts, setAlertCounts] = useState({ critical: 0, high: 0, medium: 0, low: 0, compliant: 0 });
  const [auditHistory, setAuditHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [auditResults, setAuditResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  // Multi-select and delete states
  const [selectedAudits, setSelectedAudits] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filter states: `filters` = applied (drives data fetch), `pendingFilters` = form inputs
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(userFilterKey);
      const base = saved ? { ...defaultFilters, ...JSON.parse(saved) } : defaultFilters;
      const socAlertParam = queryParams.get('socAlert');
      if (socAlertParam) {
        return { ...base, socAlert: socAlertParam };
      }
      return base;
    } catch { return defaultFilters; }
  });
  const [pendingFilters, setPendingFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(userFilterKey);
      const base = saved ? { ...defaultFilters, ...JSON.parse(saved) } : defaultFilters;
      const socAlertParam = queryParams.get('socAlert');
      if (socAlertParam) {
        return { ...base, socAlert: socAlertParam };
      }
      return base;
    } catch { return defaultFilters; }
  });

  // Sort states (user-scoped, persisted in localStorage)
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(userSortKey);
      return saved ? { ...defaultSort, ...JSON.parse(saved) } : defaultSort;
    } catch { return defaultSort; }
  });

  // Persist filters and sort to localStorage per user
  useEffect(() => {
    localStorage.setItem(userFilterKey, JSON.stringify(filters));
  }, [filters, userFilterKey]);

  useEffect(() => {
    localStorage.setItem(userSortKey, JSON.stringify(sortConfig));
  }, [sortConfig, userSortKey]);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    audit: null
  });

  // PDF Reports states
  const [loadingReports, setLoadingReports] = useState(false);

  // AI Model states
  const [allAiModels, setAllAiModels] = useState([]);

  // Pagination states
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // Summary statistics
  const [summary, setSummary] = useState({
    total_audits: 0,
    direct_audits: 0,
    average_score: 0
  });

  // Firewalls list for Device Type cascading menu
  const [firewallsList, setFirewallsList] = useState([]);

  // Get unique values for filters
  const aiModels = allAiModels;

  // Device Type display name mapping
  const deviceTypeDisplayNames = {
    'juniper_srx': 'Juniper SRX',
    'huawei_usg': 'Huawei USG',
    'fortinet_fortigate': 'Fortinet FortiGate',
    'cisco_ftd': 'Cisco FTD',
    'paloalto_panos': 'PaloAlto PAN-OS',
    'checkpoint_security_gateway': 'Checkpoint Security Gateway'
  };

  // Cascading Device Type menu state
  const [deviceMenuAnchor, setDeviceMenuAnchor] = useState(null);
  const [deviceSubmenuAnchor, setDeviceSubmenuAnchor] = useState(null);
  const [activeDeviceType, setActiveDeviceType] = useState(null);
  const deviceSubmenuTimeoutRef = useRef(null);

  const groupedDevices = (() => {
    const grouped = {};
    firewallsList.forEach((fw) => {
      const type = fw.type || 'other';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(fw);
    });
    return grouped;
  })();
  const deviceTypeOrder = ['juniper_srx', 'huawei_usg', 'fortinet_fortigate', 'cisco_ftd', 'paloalto_panos', 'checkpoint_security_gateway'];
  const sortedDeviceTypes = Object.keys(groupedDevices).sort((a, b) => {
    const ia = deviceTypeOrder.indexOf(a);
    const ib = deviceTypeOrder.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

  const handleCloseDeviceMenu = () => {
    setDeviceMenuAnchor(null);
    setDeviceSubmenuAnchor(null);
    setActiveDeviceType(null);
    if (deviceSubmenuTimeoutRef.current) clearTimeout(deviceSubmenuTimeoutRef.current);
  };


  // AI Vendor cascading menu state (inline with AIChat)
  const [vendorMenuAnchor, setVendorMenuAnchor] = useState(null);
  const [activeVendor, setActiveVendor] = useState(null);
  const [vendorSubMenuAnchor, setVendorSubMenuAnchor] = useState(null);

  const providerOrder = ['Ollama', 'OpenAI', 'Google AI Studio', 'OpenRouter', 'Groq', 'Claude (Anthropic)'];
  const groupedAiModels = (() => {
    const grouped = {};
    aiModels.forEach((model) => {
      if (!grouped[model.provider]) grouped[model.provider] = { models: [], icon: model.icon, type: model.type };
      grouped[model.provider].models.push(model);
    });
    return grouped;
  })();
  const sortedVendors = Object.keys(groupedAiModels).sort((a, b) => {
    const ia = providerOrder.indexOf(a);
    const ib = providerOrder.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

  const handleVendorMenuOpen = (event) => {
    setVendorMenuAnchor(event.currentTarget);
  };

  const handleVendorMenuClose = () => {
    setVendorMenuAnchor(null);
    setActiveVendor(null);
    setVendorSubMenuAnchor(null);
  };

  const handleVendorHover = (event, vendorKey) => {
    setActiveVendor(vendorKey);
    setVendorSubMenuAnchor(event.currentTarget);
  };

  // Get selected vendor info for display
  const getSelectedVendorInfo = () => {
    if (!pendingFilters.aiModel) return null;
    for (const vendor of sortedVendors) {
      const found = groupedAiModels[vendor]?.models.find(m => m.name === pendingFilters.aiModel);
      if (found) return { vendor: { label: vendor, icon: groupedAiModels[vendor].icon }, model: found };
    }
    return null;
  };

  const fetchAllAiModels = useCallback(async () => {
    try {
      console.log('Fetching all AI models...');

      let allModels = [];

      // Fetch offline models (Ollama)
      try {
        const offlineModels = await ollamaService.getModels();
        console.log('Offline models:', offlineModels);
        const ollamaModels = (offlineModels.models || []).map(model => ({
          name: model.name,
          displayName: model.name,
          type: 'Local',
          provider: 'Ollama',
          icon: ollamaIcon
        }));
        allModels = [...allModels, ...ollamaModels];
      } catch (err) {
        console.error('Error fetching Ollama models:', err);
      }

      // Fetch online models (OpenAI - Paid)
      try {
        const onlineModels = await openaiService.getModels();
        console.log('Online models:', onlineModels);
        const openaiModels = (onlineModels.models || []).map(model => ({
          name: model.name,
          displayName: model.display_name || model.name,
          type: 'Paid',
          provider: 'OpenAI',
          icon: chatGptIcon
        }));
        allModels = [...allModels, ...openaiModels];
      } catch (err) {
        console.error('Error fetching OpenAI models:', err);
      }

      // Fetch Google AI Studio models (Free)
      try {
        const googleResponse = await googleAiStudioService.getModels();
        console.log('Google AI Studio models:', googleResponse);
        const googleModels = (googleResponse.models || []).map(model => ({
          name: model.name,
          displayName: model.display_name || model.name,
          type: 'Free',
          provider: 'Google AI Studio',
          icon: googleAiIcon
        }));
        allModels = [...allModels, ...googleModels];
      } catch (err) {
        console.error('Error fetching Google AI Studio models:', err);
      }

      // Fetch OpenRouter models (Free)
      try {
        const openrouterResponse = await openRouterService.getModels();
        console.log('OpenRouter models:', openrouterResponse);
        const openrouterModels = (openrouterResponse.models || []).map(model => ({
          name: model.name,
          displayName: model.display_name || model.name,
          type: 'Free',
          provider: 'OpenRouter',
          icon: openRouterIcon
        }));
        allModels = [...allModels, ...openrouterModels];
      } catch (err) {
        console.error('Error fetching OpenRouter models:', err);
      }

      // Fetch Groq models (Free)
      try {
        const groqResponse = await groqService.getModels();
        console.log('Groq models:', groqResponse);
        const groqModels = (groqResponse.models || []).map(model => ({
          name: model.name,
          displayName: model.display_name || model.name,
          type: 'Free',
          provider: 'Groq',
          icon: groqIcon
        }));
        allModels = [...allModels, ...groqModels];
      } catch (err) {
        console.error('Error fetching Groq models:', err);
      }

      // Fetch Claude models (Paid)
      try {
        const claudeResponse = await claudeService.getModels();
        console.log('Claude models:', claudeResponse);
        const claudeModelsArr = (claudeResponse.models || []).map(model => ({
          name: model.name,
          displayName: model.display_name || model.name,
          type: 'Paid',
          provider: 'Claude (Anthropic)',
          icon: null
        }));
        allModels = [...allModels, ...claudeModelsArr];
      } catch (err) {
        console.error('Error fetching Claude models:', err);
      }

      // Preserve backend order within each provider (recommended first)
      // No global alphabetical sort — each provider's API returns models in priority order

      console.log('All AI models:', allModels);
      setAllAiModels(allModels);
    } catch (err) {
      console.error('Error fetching AI models:', err);
      // Set empty array on error
      setAllAiModels([]);
    }
  }, []);

  const fetchAuditHistory = useCallback(async (page = 1) => {
    let timeoutId;
    try {
      setLoading(true);
      // Set a timeout to ensure page displays after 5 seconds even if API is slow
      timeoutId = setTimeout(() => {
        setError('Loading is taking longer than expected. Some data may be incomplete.');
        setLoading(false);
      }, 5000);
      console.log('Fetching audit history with filters:', filters);
      console.log('Page:', page);
      
      const response = await firewallService.getAuditHistory(filters, page, 20);
      console.log('Fetched audit history:', response);
      console.log('Summary data:', response.summary);
      console.log('Pagination data:', response.pagination);
      
      // Debug: Check if summary exists and has the right structure
      if (response.summary) {
        console.log('Summary breakdown:', {
          total_audits: response.summary.total_audits,
          direct_audits: response.summary.direct_audits,
          average_score: response.summary.average_score
        });
      } else {
        console.warn('No summary data received from API');
      }
      
      // Debug: Check if audits array is empty
      if (!response.audits || response.audits.length === 0) {
        console.warn('No audits returned from API');
      } else {
        console.log(`Received ${response.audits.length} audits`);
      }
      
      setAuditHistory(response.audits || []);
      setPagination(response.pagination || {
        current_page: 1,
        page_size: 20,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_previous: false
      });
      setSummary(response.summary || {
        total_audits: 0,
        direct_audits: 0,
        average_score: 0
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching audit history:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText
      });
      setError('Failed to fetch audit history');
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [filters]);

  // Audit history is fetched by the [filters] effect further down, which runs
  // on mount as well as on every filter change. Fetching it here too sent the
  // same request two or three times per load.
  useEffect(() => {
    syslogService.getSummary().then(data => setAlertCounts(data)).catch(() => {});
    firewallService.listFirewalls().then(data => setFirewallsList(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAllAiModels();
  }, [fetchAllAiModels]);

  // /audit-history already carries each audit's model, so this used to be a
  // separate /firewalls/{id}/ai-models request per firewall on every load,
  // enough concurrent requests to exhaust the backend's DB pool. The fallback
  // matches what that endpoint substituted for a null model.
  const getAiModelForAudit = (audit) => {
    if (!audit) {
      return 'N/A';
    }
    return audit.ai_model || 'granite3-dense:2b';
  };

  // Same wording as the report's "AI Model Used" line: off/on is a thinking
  // switch, any other value a gpt-oss reasoning level. Null for models
  // without modes, which show the name alone.
  const getAiReasoningLabel = (audit) => {
    const reasoning = audit?.ai_reasoning;
    if (!reasoning) {
      return null;
    }
    const level = reasoning.charAt(0).toUpperCase() + reasoning.slice(1);
    return ['off', 'on'].includes(reasoning) ? `Thinking: ${level}` : `Reasoning: ${level}`;
  };

  // "loaded" when the local model was already in memory as the audit started
  // (preloaded with Load model, or already the only model loaded), "unloaded"
  // when the audit loaded it first. Null for online models and audits before
  // the load was recorded.
  const getModelLoadLabel = (audit) => {
    if (!audit?.model_load_mode) {
      return null;
    }
    return audit.model_load_mode === 'at_run' ? 'unloaded' : 'loaded';
  };

  // Caption under the model name, e.g. "Thinking: Off (loaded)"; "Loaded" or
  // "Unloaded" alone for a model without modes.
  const getAiModelCaption = (audit) => {
    const reasoning = getAiReasoningLabel(audit);
    const load = getModelLoadLabel(audit);
    if (reasoning && load) {
      return `${reasoning} (${load})`;
    }
    if (load) {
      return load.charAt(0).toUpperCase() + load.slice(1);
    }
    return reasoning;
  };

  const handleViewDetails = async (audit) => {
    setSelectedAudit(audit);
    setViewDetailsOpen(true);
    setLoadingResults(true);
    setError(null);
    try {
      console.log('Fetching results for audit:', audit.id, 'firewall:', audit.firewall_id);
      const results = await firewallService.getAuditResults(audit.firewall_id, audit.id);
      console.log('Received audit results:', results);
      if (!results || results.length === 0) {
        setError('No results found for this audit');
      } else {
        setAuditResults(results);
      }
    } catch (err) {
      console.error('Error fetching audit results:', err);
      setError(err.response?.data?.detail || 'Failed to fetch audit results');
    } finally {
      setLoadingResults(false);
    }
  };

  const handleViewReport = async (audit) => {
    try {
      setLoadingReports(true);

      console.log('Looking for report for audit:', {
        id: audit.id,
        firewall_id: audit.firewall_id,
        audit_type: audit.audit_type,
        audit_date: audit.audit_date
      });

      // First try: Direct lookup by audit_history_id (fastest)
      let matchingReport = null;
      try {
        const token = localStorage.getItem('access_token');
        const directResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/audit-reports/by-audit/${audit.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (directResponse.ok) {
          matchingReport = await directResponse.json();
          console.log('✅ Found report via direct lookup:', matchingReport);
        } else {
          console.log('Direct lookup returned:', directResponse.status);
        }
      } catch (directErr) {
        console.log('Direct lookup failed, trying pagination fallback:', directErr.message);
      }

      // Fallback: Search through paginated reports if direct lookup failed
      if (!matchingReport) {
        let page = 1;
        const maxPages = 10;

        while (page <= maxPages && !matchingReport) {
          const reportsResponse = await firewallService.getAuditReports(audit.firewall_id, page, 10);

          console.log(`Checking page ${page}, found ${reportsResponse?.reports?.length || 0} reports`);

          if (reportsResponse && reportsResponse.reports && reportsResponse.reports.length > 0) {
            matchingReport = reportsResponse.reports.find(report => {
              const reportAuditId = typeof report.audit_history_id === 'string'
                ? parseInt(report.audit_history_id, 10)
                : report.audit_history_id;
              const targetId = typeof audit.id === 'string'
                ? parseInt(audit.id, 10)
                : audit.id;
              return reportAuditId === targetId;
            });

            if (matchingReport) {
              console.log('✅ Found matching report via pagination:', matchingReport);
              break;
            }

            if (!reportsResponse.pagination?.has_next) break;
            page++;
          } else {
            break;
          }
        }
      }
      
      if (matchingReport) {
        await handleOpenReport(matchingReport.id, matchingReport.filename);
      } else {
        console.log('❌ No exact match found, trying fallback by audit type and date');

        // Fallback: Find report by audit_type and closest date
        try {
          const fallbackResponse = await firewallService.getAuditReports(audit.firewall_id, 1, 20);
          if (fallbackResponse && fallbackResponse.reports && fallbackResponse.reports.length > 0) {
            // Filter by audit type
            const typeMatchReports = fallbackResponse.reports.filter(r =>
              r.report_type === 'direct' ||
              r.filename?.includes('direct_audit_report')
            );

            if (typeMatchReports.length > 0) {
              // Find the report closest to the audit date
              const auditDate = new Date(audit.audit_date).getTime();
              let closestReport = typeMatchReports[0];
              let closestDiff = Math.abs(new Date(closestReport.generated_at).getTime() - auditDate);

              for (const report of typeMatchReports) {
                const reportDate = new Date(report.generated_at).getTime();
                const diff = Math.abs(reportDate - auditDate);
                if (diff < closestDiff) {
                  closestDiff = diff;
                  closestReport = report;
                }
              }

              // Only use fallback if within 5 minutes of audit date
              if (closestDiff <= 5 * 60 * 1000) {
                console.log('✅ Found fallback report by date proximity:', closestReport);
                await handleOpenReport(closestReport.id, closestReport.filename);
                return;
              }
            }
          }
        } catch (fallbackErr) {
          console.error('Error in fallback report search:', fallbackErr);
        }

        setSnackbar({
          open: true,
          message: 'No PDF report found for this audit. The report may still be generating.',
          severity: 'warning'
        });
      }
    } catch (err) {
      console.error('Error opening audit report:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setSnackbar({
        open: true,
        message: `Failed to open audit report: ${err.response?.data?.detail || err.message}`,
        severity: 'error'
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleOpenReport = async (reportId, filename) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${reportId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new tab
      const newWindow = window.open(url, '_blank');
      if (newWindow) {
        newWindow.focus();
      } else {
        // Fallback: download the file if popup is blocked
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbar({
          open: true,
          message: 'Popup blocked. Report downloaded instead.',
          severity: 'info'
        });
      }
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      setSnackbar({
        open: true,
        message: 'Report opened in new tab',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error opening report:', err);
      setSnackbar({
        open: true,
        message: 'Failed to open report',
        severity: 'error'
      });
    }
  };

  const formatDate = (dateString) => formatLocalDateTime(dateString);

  const formatDuration = (seconds) => {
    // Handle null/undefined duration
    if (seconds === null || seconds === undefined) return 'N/A';

    // For audits with 0 duration, show "Instant"
    if (seconds === 0) {
      return 'Instant';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    const parts = [];
    
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (remainingSeconds > 0 || parts.length === 0) {
      parts.push(`${remainingSeconds}s`);
    }
    
    return parts.join(' ');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'stopped':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    fetchAuditHistory(1);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setPendingFilters(defaultFilters);
    setSortConfig(defaultSort);
    localStorage.removeItem(userFilterKey);
    localStorage.removeItem(userSortKey);
    fetchAuditHistory(1);
  };

  // When filters change (from metric card click or active filter chip removal), fetch
  useEffect(() => {
    setPendingFilters(filters);
    fetchAuditHistory(1);
    // eslint-disable-next-line
  }, [filters]);

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filterAuditHistory = (audits) => {
    return audits.filter(audit => {
      // Device filter (frontend, specific device by ID)
      if (filters.deviceId) {
        if (audit.firewall_id !== filters.deviceId) return false;
      }
      // SOC Alert filter (frontend, derived from score)
      if (filters.socAlert) {
        const alertLevel = getAlertLevelFromScore(audit.overall_score);
        if (alertLevel !== filters.socAlert) return false;
      }
      return true;
    });
  };

  const sortAuditHistory = (audits) => {
    return [...audits].sort((a, b) => {
      let aValue, bValue;
      
      const alertSeverityOrder = {
        'Critical Alert': 4, 'High Alert': 3, 'Medium Alert': 2,
        'Low Alert': 1, 'Compliant': 0
      };

      switch (sortConfig.field) {
        case 'overall_score':
          aValue = a.overall_score ?? 0;
          bValue = b.overall_score ?? 0;
          break;
        case 'alert_level':
          aValue = alertSeverityOrder[getAlertLevelFromScore(a.overall_score)] ?? -1;
          bValue = alertSeverityOrder[getAlertLevelFromScore(b.overall_score)] ?? -1;
          break;
        case 'observations_count':
          aValue = a.observations_count ?? 0;
          bValue = b.observations_count ?? 0;
          break;
        case 'duration':
          aValue = a.duration ?? 0;
          bValue = b.duration ?? 0;
          break;
        case 'audit_date':
        default:
          aValue = new Date(a.audit_date || 0);
          bValue = new Date(b.audit_date || 0);
          break;
      }
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return null;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUpwardIcon fontSize="small" color="primary" /> : 
      <ArrowDownwardIcon fontSize="small" color="primary" />;
  };

  const SortableHeader = ({ field, children, width }) => (
    <Tooltip title={`Click to sort by ${children}`}>
      <TableCell 
        sx={{ 
          width, 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)'
          }
        }}
        onClick={() => handleSort(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {getSortIcon(field)}
        </Box>
      </TableCell>
    </Tooltip>
  );

  // Handle individual audit selection
  const handleAuditSelect = (auditId) => {
    setSelectedAudits(prev => 
      prev.includes(auditId) 
        ? prev.filter(id => id !== auditId)
        : [...prev, auditId]
    );
  };

  // Handle select all audits
  const handleSelectAll = () => {
    if (selectedAudits.length === auditHistory.length) {
      setSelectedAudits([]);
    } else {
      setSelectedAudits(auditHistory.map(audit => audit.id));
    }
  };

  // Handle delete selected audits
  const handleDeleteSelected = async () => {
    if (selectedAudits.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one audit to delete',
        severity: 'warning'
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    try {
      setDeleting(true);
      
      // Delete each selected audit
      const deletePromises = selectedAudits.map(auditId => {
        // Find the audit to get firewall_id
        const audit = auditHistory.find(a => a.id === auditId);
        if (!audit) return Promise.resolve();
        
        // Call the delete endpoint
        return firewallService.deleteAudit(audit.firewall_id, auditId);
      });
      
      await Promise.all(deletePromises);
      
      // Refresh the audit history
      await fetchAuditHistory();
      
      // Clear selection
      setSelectedAudits([]);
      
      setSnackbar({
        open: true,
        message: `Successfully deleted ${selectedAudits.length} audit report(s)`,
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error deleting audits:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to delete audit reports',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // PDF Reports functions
  const handleDownloadAuditReport = async (audit) => {
    try {
      setLoadingReports(true);
      
      // Get audit reports for this firewall
      const reportsResponse = await firewallService.getAuditReports(audit.firewall_id, 1, 10);
      
      console.log('Audit reports response:', reportsResponse);
      console.log('Looking for audit ID:', audit.id);
      
      if (reportsResponse && reportsResponse.reports && reportsResponse.reports.length > 0) {
        console.log('Available reports:', reportsResponse.reports);
        // Find the report that matches this specific audit
        const matchingReport = reportsResponse.reports.find(report => 
          report.audit_history_id === audit.id
        );
        
        console.log('Matching report:', matchingReport);
        
        if (matchingReport) {
          await handleDownloadReport(matchingReport.id, matchingReport.filename);
        } else {
          // If no specific report found, download the first (most recent) report
          const firstReport = reportsResponse.reports[0];
          console.log('No matching report found, using first report:', firstReport);
          await handleDownloadReport(firstReport.id, firstReport.filename);
        }
      } else {
        console.log('No reports found in response');
        setSnackbar({
          open: true,
          message: 'No audit reports available for this audit',
          severity: 'info'
        });
      }
    } catch (err) {
      console.error('Error downloading audit report:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setSnackbar({
        open: true,
        message: `Failed to download audit report: ${err.response?.data?.detail || err.message}`,
        severity: 'error'
      });
    } finally {
      setLoadingReports(false);
    }
  };

  const handleDownloadReport = async (reportId, filename) => {
    try {
      const blob = await firewallService.downloadAuditReport(reportId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Report downloaded successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error downloading report:', err);
      setSnackbar({
        open: true,
        message: 'Failed to download report',
        severity: 'error'
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: 1 }}>

      {/* Stats Overview - 7 cards in one row */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md>
          <Paper sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #4fc3f7 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{summary.direct_audits || 0}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Audits</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Paper sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{Math.round(summary.average_score || 0)}%</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Avg Score</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Paper
            onClick={() => setFilters(prev => ({ ...prev, socAlert: prev.socAlert === 'Critical Alert' ? '' : 'Critical Alert' }))}
            sx={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #e57373 100%)',
              color: 'white',
              boxShadow: filters.socAlert === 'Critical Alert' ? 8 : 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              outline: filters.socAlert === 'Critical Alert' ? '2px solid white' : 'none',
              p: 1.5,
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
            }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{alertCounts.critical || 0}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Critical</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Paper
            onClick={() => setFilters(prev => ({ ...prev, socAlert: prev.socAlert === 'High Alert' ? '' : 'High Alert' }))}
            sx={{
              background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
              color: 'white',
              boxShadow: filters.socAlert === 'High Alert' ? 8 : 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              outline: filters.socAlert === 'High Alert' ? '2px solid white' : 'none',
              p: 1.5,
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
            }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{alertCounts.high || 0}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>High</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Paper
            onClick={() => setFilters(prev => ({ ...prev, socAlert: prev.socAlert === 'Medium Alert' ? '' : 'Medium Alert' }))}
            sx={{
              background: 'linear-gradient(135deg, #fbc02d 0%, #fff176 100%)',
              color: 'rgba(0,0,0,0.87)',
              boxShadow: filters.socAlert === 'Medium Alert' ? 8 : 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              outline: filters.socAlert === 'Medium Alert' ? '2px solid rgba(0,0,0,0.87)' : 'none',
              p: 1.5,
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
            }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{alertCounts.medium || 0}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Medium</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Paper
            onClick={() => setFilters(prev => ({ ...prev, socAlert: prev.socAlert === 'Low Alert' ? '' : 'Low Alert' }))}
            sx={{
              background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
              color: 'white',
              boxShadow: filters.socAlert === 'Low Alert' ? 8 : 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              outline: filters.socAlert === 'Low Alert' ? '2px solid white' : 'none',
              p: 1.5,
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
            }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{alertCounts.low || 0}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Low</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Paper
            onClick={() => setFilters(prev => ({ ...prev, socAlert: prev.socAlert === 'Compliant' ? '' : 'Compliant' }))}
            sx={{
              background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
              color: 'white',
              boxShadow: filters.socAlert === 'Compliant' ? 8 : 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
              outline: filters.socAlert === 'Compliant' ? '2px solid white' : 'none',
              p: 1.5,
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
            }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{alertCounts.compliant || 0}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Compliant</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Active Filters Bar */}
      {(() => {
        const activeFilters = [];
        if (filters.socAlert) activeFilters.push({ label: `Alert: ${filters.socAlert}`, key: 'socAlert' });
        if (filters.benchmarkType) activeFilters.push({ label: `Vendor: ${deviceTypeDisplayNames[filters.benchmarkType] || filters.benchmarkType}`, key: 'benchmarkType' });
        if (filters.deviceId) {
          const fw = firewallsList.find(f => f.id === filters.deviceId);
          activeFilters.push({ label: `Device: ${fw?.device_name || filters.deviceId}`, key: 'deviceId' });
        }
        if (filters.aiModel) activeFilters.push({ label: `Model: ${filters.aiModel}`, key: 'aiModel' });
        if (filters.reportStatus) activeFilters.push({ label: `Status: ${filters.reportStatus === 'success' ? 'Success' : 'Failure'}`, key: 'reportStatus' });
        if (filters.startDate) activeFilters.push({ label: `From: ${filters.startDate}`, key: 'startDate' });
        if (filters.endDate) activeFilters.push({ label: `To: ${filters.endDate}`, key: 'endDate' });
        if (filters.minScore) activeFilters.push({ label: `Score ≥ ${filters.minScore}%`, key: 'minScore' });
        if (filters.maxScore) activeFilters.push({ label: `Score ≤ ${filters.maxScore}%`, key: 'maxScore' });
        if (filters.obsCategory) activeFilters.push({ label: `Observations: ${filters.obsCategory}`, key: 'obsCategory' });
        if (activeFilters.length === 0) return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>Active Filters:</Typography>
            {activeFilters.map(f => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                color="primary"
                onDelete={() => setFilters(prev => ({ ...prev, [f.key]: '' }))}
                sx={{ fontSize: '0.75rem', height: 24 }}
              />
            ))}
            {activeFilters.length > 1 && (
              <Chip
                label="Clear All"
                size="small"
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ fontSize: '0.75rem', height: 24, cursor: 'pointer' }}
              />
            )}
          </Box>
        );
      })()}

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Start Date"
              type="date"
              value={pendingFilters.startDate}
              onChange={(e) => setPendingFilters({ ...pendingFilters, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="End Date"
              type="date"
              value={pendingFilters.endDate}
              onChange={(e) => setPendingFilters({ ...pendingFilters, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Device Type"
              value={
                pendingFilters.deviceId
                  ? (firewallsList.find(fw => fw.id === pendingFilters.deviceId)?.device_name || 'All')
                  : (pendingFilters.benchmarkType ? (deviceTypeDisplayNames[pendingFilters.benchmarkType] || pendingFilters.benchmarkType) : 'All')
              }
              onClick={(e) => setDeviceMenuAnchor(e.currentTarget)}
              InputProps={{
                readOnly: true,
                endAdornment: <ArrowDropDownIcon sx={{ color: 'action.active' }} />,
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
            />
            <Menu
              anchorEl={deviceMenuAnchor}
              open={Boolean(deviceMenuAnchor)}
              onClose={handleCloseDeviceMenu}
              PaperProps={{ sx: { width: 260, mt: 0.5 } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                selected={!pendingFilters.benchmarkType && !pendingFilters.deviceId}
                onClick={() => { setPendingFilters({ ...pendingFilters, benchmarkType: '', deviceId: '' }); handleCloseDeviceMenu(); }}
              >
                All
              </MenuItem>
              {sortedDeviceTypes.map((type) => (
                <MenuItem
                  key={type}
                  onMouseEnter={(e) => {
                    if (deviceSubmenuTimeoutRef.current) clearTimeout(deviceSubmenuTimeoutRef.current);
                    setDeviceSubmenuAnchor(e.currentTarget);
                    setActiveDeviceType(type);
                  }}
                  onMouseLeave={() => {
                    deviceSubmenuTimeoutRef.current = setTimeout(() => {
                      setDeviceSubmenuAnchor(null);
                      setActiveDeviceType(null);
                    }, 200);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: activeDeviceType === type ? 'action.hover' : 'transparent'
                  }}
                >
                  <Typography variant="body2">{deviceTypeDisplayNames[type] || type}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={groupedDevices[type].length}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
                    />
                    <KeyboardArrowRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            <Popper
              open={Boolean(deviceSubmenuAnchor) && Boolean(activeDeviceType)}
              anchorEl={deviceSubmenuAnchor}
              placement="right-start"
              sx={{ zIndex: 1301 }}
            >
              <Paper
                elevation={8}
                sx={{ maxHeight: 300, overflowY: 'auto', minWidth: 220 }}
                onMouseEnter={() => {
                  if (deviceSubmenuTimeoutRef.current) clearTimeout(deviceSubmenuTimeoutRef.current);
                }}
                onMouseLeave={() => {
                  deviceSubmenuTimeoutRef.current = setTimeout(() => {
                    setDeviceSubmenuAnchor(null);
                    setActiveDeviceType(null);
                  }, 200);
                }}
              >
                <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: '32px', fontWeight: 'bold', color: 'text.primary' }}>
                  {deviceTypeDisplayNames[activeDeviceType] || activeDeviceType}
                </ListSubheader>
                <MenuItem
                  selected={pendingFilters.benchmarkType === activeDeviceType && !pendingFilters.deviceId}
                  onClick={() => {
                    setPendingFilters({ ...pendingFilters, benchmarkType: activeDeviceType, deviceId: '' });
                    handleCloseDeviceMenu();
                  }}
                >
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>All {deviceTypeDisplayNames[activeDeviceType] || activeDeviceType}</Typography>
                </MenuItem>
                {activeDeviceType && groupedDevices[activeDeviceType]?.map((fw) => (
                  <MenuItem
                    key={fw.id}
                    selected={pendingFilters.deviceId === fw.id}
                    onClick={() => {
                      setPendingFilters({ ...pendingFilters, benchmarkType: activeDeviceType, deviceId: fw.id });
                      handleCloseDeviceMenu();
                    }}
                  >
                    <Box>
                      <Typography variant="body2" noWrap>{fw.device_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{fw.ip_address}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Paper>
            </Popper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>SOC Alert</InputLabel>
              <Select
                value={pendingFilters.socAlert}
                onChange={(e) => setPendingFilters({ ...pendingFilters, socAlert: e.target.value })}
                label="SOC Alert"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Critical Alert">Critical Alert</MenuItem>
                <MenuItem value="High Alert">High Alert</MenuItem>
                <MenuItem value="Medium Alert">Medium Alert</MenuItem>
                <MenuItem value="Low Alert">Low Alert</MenuItem>
                <MenuItem value="Compliant">Compliant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel shrink>AI Vendor</InputLabel>
              <Select
                value=""
                open={false}
                onOpen={handleVendorMenuOpen}
                label="AI Vendor"
                displayEmpty
                sx={{ borderRadius: 2 }}
                renderValue={() => {
                  const info = getSelectedVendorInfo();
                  if (!info) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StorefrontIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                        <Typography variant="body2" sx={{ opacity: 0.5 }}>All</Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {info.vendor.icon ? (
                        <Box component="img" src={info.vendor.icon} alt={info.vendor.label} sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'contain' }} />
                      ) : (
                        <AutoAwesomeIcon sx={{ width: 20, height: 20, color: '#D97706' }} />
                      )}
                      <Typography variant="body2" noWrap>{info.model.displayName}</Typography>
                    </Box>
                  );
                }}
              >
                <MenuItem value="" disabled />
              </Select>
            </FormControl>

            {/* AI Vendor Main Menu */}
            <Menu
              anchorEl={vendorMenuAnchor}
              open={Boolean(vendorMenuAnchor)}
              onClose={handleVendorMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={{ '& .MuiPaper-root': { minWidth: 240, mt: 0.5 } }}
            >
              <MenuItem
                selected={!pendingFilters.aiModel}
                onClick={() => { setPendingFilters({ ...pendingFilters, aiModel: '' }); handleVendorMenuClose(); }}
                sx={{ py: 1.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <StorefrontIcon sx={{ fontSize: 24, opacity: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>All Vendors</Typography>
                </Box>
              </MenuItem>
              {sortedVendors.map((vendor) => (
                <MenuItem
                  key={vendor}
                  onMouseEnter={(e) => handleVendorHover(e, vendor)}
                  sx={{
                    py: 1.5,
                    backgroundColor: activeVendor === vendor ? 'action.hover' : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    {groupedAiModels[vendor].icon ? (
                      <Box component="img" src={groupedAiModels[vendor].icon} alt={vendor} sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }} />
                    ) : (
                      <AutoAwesomeIcon sx={{ width: 24, height: 24, color: '#D97706' }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{vendor}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${groupedAiModels[vendor].models.length} model${groupedAiModels[vendor].models.length !== 1 ? 's' : ''}`}
                      </Typography>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                  </Box>
                </MenuItem>
              ))}
            </Menu>

            {/* AI Vendor Sub Menu - Models */}
            <Menu
              anchorEl={vendorSubMenuAnchor}
              open={Boolean(vendorSubMenuAnchor) && Boolean(activeVendor)}
              onClose={handleVendorMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={{
                pointerEvents: 'none',
                '& .MuiPaper-root': {
                  pointerEvents: 'auto',
                  minWidth: 300,
                  maxHeight: 400,
                  overflow: 'auto',
                  ml: 0.5,
                }
              }}
            >
              {activeVendor && groupedAiModels[activeVendor] && (
                groupedAiModels[activeVendor].models.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">No models available</Typography>
                  </MenuItem>
                ) : (
                  groupedAiModels[activeVendor].models.map((model) => (
                    <MenuItem
                      key={model.name}
                      onClick={() => {
                        setPendingFilters({ ...pendingFilters, aiModel: model.name });
                        handleVendorMenuClose();
                      }}
                      selected={pendingFilters.aiModel === model.name}
                      sx={{ py: 1 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {groupedAiModels[activeVendor].icon ? (
                          <Box component="img" src={groupedAiModels[activeVendor].icon} alt={activeVendor} sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'contain' }} />
                        ) : (
                          <AutoAwesomeIcon sx={{ width: 20, height: 20, color: '#D97706' }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" noWrap>{model.displayName}</Typography>
                          <Typography variant="caption" color="text.secondary">{model.type} model</Typography>
                        </Box>
                        {model.recommended && (
                          <Typography variant="caption" color="secondary" sx={{ fontWeight: 'bold' }}>Recommended</Typography>
                        )}
                        {pendingFilters.aiModel === model.name && (
                          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: 10, color: 'white' }}>✓</Typography>
                          </Box>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                )
              )}
            </Menu>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={pendingFilters.reportStatus}
                onChange={(e) => setPendingFilters({ ...pendingFilters, reportStatus: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failure">Failure</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Action buttons row */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant={showAdvancedFilters ? 'contained' : 'outlined'}
                size="small"
                startIcon={<TuneIcon />}
                endIcon={<ExpandMoreIcon sx={{ transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ textTransform: 'none' }}
              >
                Advanced Filters
                {(pendingFilters.maxScore || pendingFilters.minScore || pendingFilters.maxDuration || pendingFilters.minDuration || pendingFilters.maxObs || pendingFilters.minObs || pendingFilters.obsCategory) && (
                  <Chip
                    label={[pendingFilters.maxScore, pendingFilters.minScore, pendingFilters.maxDuration, pendingFilters.minDuration, pendingFilters.maxObs, pendingFilters.minObs, pendingFilters.obsCategory].filter(Boolean).length}
                    size="small"
                    color="warning"
                    sx={{ ml: 1, height: 18, fontSize: '0.65rem', minWidth: 18 }}
                  />
                )}
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>

          {/* Collapsible Advanced Filters */}
          <Grid item xs={12} sx={{ pt: showAdvancedFilters ? undefined : '0 !important' }}>
            <Collapse in={showAdvancedFilters} timeout={300}>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {/* Row 1: Max filters */}
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="Max Score (%)"
                    type="number"
                    value={pendingFilters.maxScore}
                    onChange={(e) => setPendingFilters({ ...pendingFilters, maxScore: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0, max: 100 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="Min Score (%)"
                    type="number"
                    value={pendingFilters.minScore}
                    onChange={(e) => setPendingFilters({ ...pendingFilters, minScore: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0, max: 100 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="Max Dur. (sec)"
                    type="number"
                    value={pendingFilters.maxDuration}
                    onChange={(e) => setPendingFilters({ ...pendingFilters, maxDuration: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="Min Dur. (sec)"
                    type="number"
                    value={pendingFilters.minDuration}
                    onChange={(e) => setPendingFilters({ ...pendingFilters, minDuration: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="Max Obs."
                    type="number"
                    value={pendingFilters.maxObs}
                    onChange={(e) => setPendingFilters({ ...pendingFilters, maxObs: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    label="Min Obs."
                    type="number"
                    value={pendingFilters.minObs}
                    onChange={(e) => setPendingFilters({ ...pendingFilters, minObs: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Obs Severity</InputLabel>
                    <Select
                      value={pendingFilters.obsCategory}
                      onChange={(e) => setPendingFilters({ ...pendingFilters, obsCategory: e.target.value })}
                      label="Obs Severity"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="CRITICAL">Critical</MenuItem>
                      <MenuItem value="HIGH">High</MenuItem>
                      <MenuItem value="MEDIUM">Medium</MenuItem>
                      <MenuItem value="LOW">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </Paper>

      {auditHistory.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No audit history available
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Bulk Actions */}
          {selectedAudits.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body1">
                  {selectedAudits.length} audit report(s) selected
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  size="small"
                >
                  Delete Selected
                </Button>
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {pagination.total_count === 0 ? (
                'No reports'
              ) : pagination.total_count <= pagination.page_size ? (
                <>Showing <strong>{auditHistory.length}</strong> {auditHistory.length === 1 ? 'report' : 'reports'}</>
              ) : (
                <>
                  Showing <strong>{((pagination.current_page - 1) * pagination.page_size) + 1}</strong>
                  &ndash;<strong>{Math.min(pagination.current_page * pagination.page_size, pagination.total_count)}</strong>
                  {' '}of <strong>{pagination.total_count}</strong> reports
                </>
              )}
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', maxWidth: '100vw' }}>
            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: '4%' }}>
                    <Checkbox
                      indeterminate={selectedAudits.length > 0 && selectedAudits.length < auditHistory.length}
                      checked={selectedAudits.length === auditHistory.length && auditHistory.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <SortableHeader field="audit_date" width="12%">Date</SortableHeader>
                  <TableCell sx={{ width: '15%' }}>Firewall</TableCell>
                  <SortableHeader field="overall_score" width="7%">Score</SortableHeader>
                  <SortableHeader field="alert_level" width="10%">SOC Alert</SortableHeader>
                  <SortableHeader field="observations_count" width="5%">Obs.</SortableHeader>
                  <SortableHeader field="duration" width="8%">Duration</SortableHeader>
                  <TableCell sx={{ width: '18%' }}>AI Model</TableCell>
                  <TableCell sx={{ width: '11%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortAuditHistory(filterAuditHistory(auditHistory.filter(audit => audit && audit.firewall_id))).map((audit) => (
                  <TableRow key={audit.id}>
                    <TableCell padding="checkbox" sx={{ width: '4%' }}>
                      <Checkbox
                        checked={selectedAudits.includes(audit.id)}
                        onChange={() => handleAuditSelect(audit.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>{formatDate(audit.audit_date)}</TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {audit.firewall?.device_name}
                        </Typography>
                        {audit.firewall?.ip_address && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {audit.firewall.ip_address}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '7%' }}>
                      {audit.overall_score !== null ? `${audit.overall_score.toFixed(1)}%` : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ width: '10%' }}>
                      {getAlertLevelFromScore(audit.overall_score)
                        ? <Chip label={getAlertLevelFromScore(audit.overall_score)} size="small" sx={{ ...getAlertChipStyle(getAlertLevelFromScore(audit.overall_score)), fontWeight: 600, fontSize: '0.7rem' }} />
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ width: '5%' }}>
                      <Tooltip
                        title={
                          audit.observations_breakdown && Object.keys(audit.observations_breakdown).length > 0 ? (
                            <Box sx={{ p: 0.5 }}>
                              {Object.entries(audit.observations_breakdown)
                                .sort(([a], [b]) => {
                                  const order = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
                                  return (order[a] ?? 4) - (order[b] ?? 4);
                                })
                                .map(([severity, count]) => {
                                  const colors = {
                                    'CRITICAL': '#d32f2f',
                                    'HIGH': '#f57c00',
                                    'MEDIUM': '#fbc02d',
                                    'LOW': '#388e3c'
                                  };
                                  return (
                                    <Typography key={severity} variant="caption" sx={{ display: 'block', color: colors[severity] || 'inherit' }}>
                                      {severity}: {count}
                                    </Typography>
                                  );
                                })
                              }
                            </Box>
                          ) : 'No breakdown available'
                        }
                        arrow
                        placement="top"
                      >
                        <Typography variant="body2" fontWeight="bold" sx={{ cursor: 'pointer', color: '#d32f2f' }}>
                          {audit.observations_count || 0}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: '8%' }}>
                      {formatDuration(audit.duration)}
                      {/* Duration is audit time only; a load at run time is shown under it */}
                      {audit.model_load_mode === 'at_run' && audit.model_load_seconds > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          +{Math.round(audit.model_load_seconds)} s load
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: '18%' }}>
                      <Tooltip title={getAiModelForAudit(audit)}>
                        <Box>
                          <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getAiModelForAudit(audit)}
                          </Typography>
                          {getAiModelCaption(audit) && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {getAiModelCaption(audit)}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: '11%' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Report">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewReport(audit)}
                            disabled={loadingReports}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Report">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadAuditReport(audit)}
                            disabled={loadingReports}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination Controls */}
          {pagination.total_pages > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Previous Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchAuditHistory(pagination.current_page - 1)}
                  disabled={!pagination.has_previous || loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  Previous
                </Button>
                
                {/* Page Numbers */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pagination.current_page === pageNum ? "contained" : "outlined"}
                        size="small"
                        onClick={() => fetchAuditHistory(pageNum)}
                        disabled={loading}
                        sx={{ minWidth: 40 }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </Box>
                
                {/* Next Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchAuditHistory(pagination.current_page + 1)}
                  disabled={!pagination.has_next || loading}
                  endIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={viewDetailsOpen}
        onClose={() => setViewDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Audit Details - {selectedAudit?.firewall?.device_name}
        </DialogTitle>
        <DialogContent>
          {loadingResults ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Firewall"
                      secondary={selectedAudit?.firewall?.device_name}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Type"
                      secondary={selectedAudit?.firewall?.type}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Overall Score"
                      secondary={`${selectedAudit?.overall_score?.toFixed(1)}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Duration"
                      secondary={formatDuration(selectedAudit?.duration)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Total Checks"
                      secondary={selectedAudit?.total_checks}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Passed Checks"
                      secondary={selectedAudit?.passed_checks}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Failed Checks"
                      secondary={selectedAudit?.failed_checks}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="AI Model"
                      secondary={getAiModelForAudit(selectedAudit)}
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider />

              <Box mt={3}>
                <Typography variant="h6" gutterBottom>Checks</Typography>
                {auditResults.map((result, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" width="100%">
                        <Chip
                          label={result.status}
                          color={result.status === 'PASS' ? 'success' : 'error'}
                          size="small"
                          style={{ marginRight: 16 }}
                        />
                        <Typography>
                          {result.check_id}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" gutterBottom>
                        Details:
                      </Typography>
                      <Typography paragraph>
                        {result.details}
                      </Typography>
                      {result.remediation_steps && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Remediation Steps:
                          </Typography>
                          <Typography>
                            {result.remediation_steps}
                          </Typography>
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>

              {selectedAudit?.error_message && (
                <Box mt={3}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Error Message
                  </Typography>
                  <Typography color="error">
                    {selectedAudit.error_message}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, audit: null })}
        maxWidth="md"
        fullWidth
      >
        {detailsDialog.audit && (
          <>
            <DialogTitle>
              Audit Details
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Firewall</Typography>
                  <Typography variant="body1">
                    {detailsDialog.audit.firewall.device_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Date</Typography>
                  <Typography variant="body1">
                    {formatDate(detailsDialog.audit.audit_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Status</Typography>
                  <Typography variant="body1">
                    {detailsDialog.audit.status}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Score</Typography>
                  <Typography variant="body1">
                    {detailsDialog.audit.overall_score !== null
                      ? `${detailsDialog.audit.overall_score.toFixed(1)}%`
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Duration</Typography>
                  <Typography variant="body1">
                    {formatDuration(detailsDialog.audit.duration)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Checks</Typography>
                  <Typography variant="body1">
                    Total: {detailsDialog.audit.total_checks}
                    <br />
                    Passed: {detailsDialog.audit.passed_checks}
                    <br />
                    Failed: {detailsDialog.audit.failed_checks}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">AI Model</Typography>
                  <Typography variant="body1">
                    {getAiModelForAudit(detailsDialog.audit)}
                  </Typography>
                </Grid>
                {detailsDialog.audit.error_message && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Error</Typography>
                    <Typography variant="body1" color="error">
                      {detailsDialog.audit.error_message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDetailsDialog({ open: false, audit: null })}
                color="primary"
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedAudits.length} selected audit report(s)?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default History; 